// use-url-search-params@2.4.3 - 2021-12-27T06:05:04.545Z
(function (global, factory) {
    typeof exports === 'object' && typeof module !== 'undefined' ? factory(exports, require('react')) :
    typeof define === 'function' && define.amd ? define(['exports', 'react'], factory) :
    (global = typeof globalThis !== 'undefined' ? globalThis : global || self, factory(global.useUrlSearchParams = {}, global.React));
})(this, (function (exports, react) { 'use strict';

    /*! *****************************************************************************
    Copyright (c) Microsoft Corporation.

    Permission to use, copy, modify, and/or distribute this software for any
    purpose with or without fee is hereby granted.

    THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
    REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
    AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
    INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
    LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
    OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
    PERFORMANCE OF THIS SOFTWARE.
    ***************************************************************************** */

    var __assign = function() {
        __assign = Object.assign || function __assign(t) {
            for (var s, i = 1, n = arguments.length; i < n; i++) {
                s = arguments[i];
                for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
            }
            return t;
        };
        return __assign.apply(this, arguments);
    };

    var noop = function () { };
    var listeners = {
        popstate: []
    };
    var mockWindow = {
        URL: {
            searchParams: {
                set: noop,
                append: noop,
                "delete": noop
            }
        },
        location: {
            href: "",
            search: ""
        },
        history: {
            pushState: noop
        },
        fireEvent: function (eventName) {
            listeners[eventName].forEach(function (listener) {
                listener();
            });
        },
        addEventListener: function (eventName, listener) {
            listeners[eventName].push(listener);
        },
        removeEventListener: function (eventName, listener) {
            var eventListeners = listeners[eventName];
            var i = eventListeners.findIndex(function (l) { return l === listener; });
            eventListeners.splice(i, 1);
        }
    };
    /**
     * @returns {Window}
     */
    function getWindow() {
        if (typeof window !== "undefined")
            return window;
        return mockWindow;
    }

    var SUPPORTED_PARAMS_TYPES = [Number, String, Boolean, Date];
    function setQueryToCurrentUrl(params) {
        var URL = getWindow().URL;
        var url = new URL(getWindow().location.href);
        Object.keys(params).forEach(function (key) {
            var value = params[key];
            if (value !== null && value !== undefined) {
                if (Array.isArray(value)) {
                    url.searchParams["delete"](key);
                    value.forEach(function (valueItem) {
                        url.searchParams.append(key, valueItem);
                    });
                }
                else if (value instanceof Date) {
                    if (!isNaN(value.getTime())) {
                        url.searchParams.set(key, value.toISOString());
                    }
                }
                else if (typeof value === "object") {
                    url.searchParams.set(key, JSON.stringify(value));
                }
                else {
                    url.searchParams.set(key, value);
                }
            }
            else {
                url.searchParams["delete"](key);
            }
        });
        return url;
    }
    function isNoneEmptyPrimitiveArray(input) {
        return (Array.isArray(input) &&
            input.length > 0 &&
            input.every(function (item) { return typeof item === "number" || typeof item === "string" || typeof item === "boolean"; }));
    }
    function validateTypes(types) {
        if (types === void 0) { types = {}; }
        var isValidTypes = Object.values(types).every(function (type) {
            return SUPPORTED_PARAMS_TYPES.includes(type) ||
                isNoneEmptyPrimitiveArray(type) ||
                typeof type === "function";
        });
        if (!isValidTypes) {
            throw new Error("Unsupported param types. Must be one of [".concat(SUPPORTED_PARAMS_TYPES.map(function (item) { return item.name; }).join(", "), "]"));
        }
    }
    function useUrlSearchParams(initial, types) {
        if (initial === void 0) { initial = {}; }
        if (types)
            validateTypes(types);
        /**
         * The main idea of this hook is to make things response to change of `window.location.search`,
         * so no need for introducing new state (in the mean time).
         * Whenever `window.location.search` is changed but  not cause re-render, call `forceUpdate()`.
         * Whenever the component - user of this hook - re-render, this hook should return
         * the query object that corresponse to the current `window.location.search`
         */
        var _a = react.useState(), forceUpdate = _a[1];
        var locationSearch = getWindow().location.search;
        var urlSearchParams = react.useMemo(function () {
            return new URLSearchParams(locationSearch);
        }, [locationSearch]);
        var params = react.useMemo(function () {
            var result = [];
            urlSearchParams.forEach(function (value, key) {
                result.push({
                    key: key,
                    value: value
                });
            });
            //group by key
            result = result.reduce(function (acc, val) {
                (acc[val.key] = acc[val.key] || []).push(val);
                return acc;
            }, {});
            result = Object.keys(result).map(function (key) {
                var valueGroup = result[key];
                if (valueGroup.length === 1) {
                    return [key, valueGroup[0].value];
                }
                else {
                    return [key, valueGroup.map(function (_a) {
                            var value = _a.value;
                            return value;
                        })];
                }
            });
            var params = __assign({}, initial);
            result.forEach(function (_a) {
                var key = _a[0], value = _a[1];
                params[key] = parseValue(key, value, types, initial);
            });
            return params;
        }, [urlSearchParams]);
        function redirectToNewSearchParams(params) {
            var url = setQueryToCurrentUrl(params);
            if (getWindow().location.search !== url.search) {
                getWindow().history.pushState({}, "", url.toString());
            }
            if (urlSearchParams.toString() !== url.searchParams.toString()) {
                forceUpdate({});
            }
        }
        react.useEffect(function () {
            redirectToNewSearchParams(__assign(__assign({}, initial), params));
        }, [params]);
        var setParams = function (params) {
            redirectToNewSearchParams(params);
        };
        react.useEffect(function () {
            var onPopState = function () {
                forceUpdate({});
            };
            getWindow().addEventListener("popstate", onPopState);
            return function () {
                getWindow().removeEventListener("popstate", onPopState);
            };
        }, []);
        return [params, setParams];
    }
    var booleanValues = {
        "true": true,
        "false": false
    };
    function parseValue(key, _value, types, defaultParams) {
        if (!types)
            return _value;
        var type = types[key];
        var value = _value === undefined ? defaultParams[key] : _value;
        if (type === Number) {
            return Number(value);
        }
        if (type === Boolean) {
            return booleanValues[value];
        }
        if (type === Date) {
            return new Date(value);
        }
        if (Array.isArray(type)) {
            // eslint-disable-next-line eqeqeq
            return type.find(function (item) { return item == value; }) || defaultParams[key];
        }
        if (typeof type === "function") {
            return type(value);
        }
        return value;
    }

    exports.useUrlSearchParams = useUrlSearchParams;

    Object.defineProperty(exports, '__esModule', { value: true });

}));
